import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import type { ColumnDef } from "@tanstack/react-table";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { Loader2, MessageSquare, UsersRound } from "lucide-react";

import { DataTable } from "@/shared/components/data/DataTable";
import { DataToolbar } from "@/shared/components/data/DataToolbar";
import { Pagination } from "@/shared/components/data/Pagination";
import { StatusPill } from "@/shared/components/data/StatusPill";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { useQueryState } from "@/shared/hooks/use-query-state";
import { cn } from "@/shared/lib/utils";
import { Stage, useListCircleGroupsQuery } from "@/store/apis/adminApi.enhanced";
import type { CircleGroup } from "@/store/apis/adminApi.enhanced";
import type { ListCircleGroupsApiArg } from "@/store/apis/adminApi.generated";

type Sort = NonNullable<ListCircleGroupsApiArg["sort"]>;
type StatusFilter = NonNullable<ListCircleGroupsApiArg["status"]>;

const DEFAULT_LIMIT = 25;

const STAGE_LABEL: Record<Stage, string> = {
  [Stage.Trying]: "Trying",
  [Stage.Fertility]: "Fertilidad",
  [Stage.Loss]: "Pérdida",
  [Stage.Lost]: "Lost",
};

export function CircleGroupsPage() {
  const navigate = useNavigate();
  const { get, set } = useQueryState();

  const search = get("q", "");
  const debouncedSearch = useDebouncedValue(search, 300);
  const stageFilter = get("stage", "");
  const statusFilter = get("status", "");
  const sort = (get("sort", "last_activity_at") || "last_activity_at") as Sort;
  const offset = Number(get("offset", "0")) || 0;
  const limit = Number(get("limit", String(DEFAULT_LIMIT))) || DEFAULT_LIMIT;

  const filtersActive =
    debouncedSearch.trim().length > 0 || Boolean(stageFilter) || Boolean(statusFilter);

  const { data, isLoading, isFetching, isError, error, refetch } = useListCircleGroupsQuery({
    q: debouncedSearch || undefined,
    stage: (stageFilter as Stage) || undefined,
    status: (statusFilter as StatusFilter) || undefined,
    sort,
    offset,
    limit,
  });

  const columns = useMemo<ColumnDef<CircleGroup>[]>(
    () => [
      {
        id: "group",
        header: "Grupo",
        cell: ({ row }) => {
          const g = row.original;
          return (
            <div className="flex items-center gap-3">
              <div
                aria-hidden
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl font-display text-[13px] font-medium text-primary-foreground"
                style={{ background: g.avatar_bg || "#3d8f5f" }}
              >
                {g.title.slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="truncate text-[13.5px] font-medium text-foreground">{g.title}</p>
                <p className="truncate font-mono text-[11px] text-muted-foreground">
                  {g.id.slice(0, 8)}…
                </p>
              </div>
            </div>
          );
        },
      },
      {
        id: "stages",
        header: "Etapas",
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.stages.map((s) => (
              <span
                key={s}
                className="inline-flex rounded border border-border/60 bg-muted/40 px-1.5 py-0.5 font-mono text-[10.5px] text-foreground"
              >
                {STAGE_LABEL[s]}
              </span>
            ))}
          </div>
        ),
      },
      {
        id: "members",
        header: "Miembros",
        cell: ({ row }) => (
          <span className="font-mono text-[12.5px] tabular-nums text-foreground">
            {row.original.member_count.toLocaleString("es-PE")}
          </span>
        ),
      },
      {
        id: "status",
        header: "Estado",
        cell: ({ row }) => (
          <StatusPill tone={row.original.status === "active" ? "success" : "muted"}>
            {row.original.status === "active" ? "Activo" : "Archivado"}
          </StatusPill>
        ),
      },
      {
        id: "last_activity_at",
        header: "Última actividad",
        cell: ({ row }) => (
          <span className="font-mono text-[12px] text-muted-foreground">
            hace {formatDistanceToNow(new Date(row.original.last_activity_at), { locale: es })}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        kicker="Comunidad"
        title="Grupos del Círculo"
        description="Comunidades por etapa en el app móvil. Monitorea actividad, miembros y reportes."
      />

      <DataToolbar
        search={search}
        onSearchChange={(next) => set({ q: next, offset: 0 })}
        searchPlaceholder="Buscar por título…"
        filters={
          <>
            <Select
              value={stageFilter || "all"}
              onValueChange={(v) => set({ stage: v === "all" ? "" : v, offset: 0 })}
            >
              <SelectTrigger className="h-10 w-[160px] text-[13px]">
                <SelectValue placeholder="Etapa" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las etapas</SelectItem>
                {Object.values(Stage).map((s) => (
                  <SelectItem key={s} value={s}>
                    {STAGE_LABEL[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={statusFilter || "all"}
              onValueChange={(v) => set({ status: v === "all" ? "" : v, offset: 0 })}
            >
              <SelectTrigger className="h-10 w-[140px] text-[13px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Activos</SelectItem>
                <SelectItem value="archived">Archivados</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sort ?? "last_activity_at"} onValueChange={(v) => set({ sort: v })}>
              <SelectTrigger className="h-10 w-[200px] text-[13px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="last_activity_at">Actividad reciente</SelectItem>
                <SelectItem value="member_count">Más miembros</SelectItem>
                <SelectItem value="created_at">Creado reciente</SelectItem>
              </SelectContent>
            </Select>
          </>
        }
        actions={
          isFetching && !isLoading ? (
            <span
              className={cn(
                "inline-flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground",
              )}
            >
              <Loader2 className="h-3 w-3 animate-spin" />
              Actualizando
            </span>
          ) : null
        }
      />

      <DataTable
        columns={columns}
        data={data?.items}
        loading={isLoading}
        error={isError ? error : undefined}
        onRetry={() => void refetch()}
        onRowClick={(g) => navigate(`/circulo/grupos/${g.id}`)}
        getRowId={(row) => row.id}
        filtersActive={filtersActive}
        empty={{
          icon: <UsersRound className="h-6 w-6" />,
          title: "Aún no hay grupos",
          description: (
            <>
              Los grupos se crean desde el app móvil y aparecen aquí para que puedas monitorearlos.
            </>
          ),
        }}
        noResults={{
          actions: (
            <button
              type="button"
              onClick={() => set({ q: "", stage: "", status: "", offset: 0 })}
              className="font-mono text-[12px] font-medium text-primary underline-offset-4 hover:underline"
            >
              Limpiar filtros
            </button>
          ),
        }}
      />

      <Pagination
        total={data?.total ?? 0}
        limit={limit}
        offset={offset}
        onOffsetChange={(next) => set({ offset: next })}
        onLimitChange={(next) => set({ limit: next })}
      />

      {/* Hint sobre la columna reports (hay un pending_reports_count en el detail) */}
      {data?.items.length ? (
        <p className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
          <MessageSquare className="h-3 w-3" />
          Entra a un grupo para ver estadísticas de mensajes y reportes.
        </p>
      ) : null}
    </>
  );
}
